// src/components/settings/Settings.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import GeneralSettings from './GeneralSettings';
import EmailSettings from './EmailSettings';
import EmailTemplates from './EmailTemplates';
import CountryManagement from './CountryManagement';
import CityManagement from './CityManagement';
import DesignationManagement from './DesignationManagement';
import PublicHolidayManagement from './PublicHolidayManagement';
import SalesAgentManagement from './SalesAgentManagement';
import IntegrationSettings from './IntegrationSettings';

const Settings = () => {
    usePageTitle('Settings');
    const { user } = useAuth();
    const location = useLocation();

    // Build tabs based on user role
    const tabs = [
        { id: 'general', label: 'General', icon: 'fas fa-cog', roles: ['core_admin'] },
        { id: 'email', label: 'Email Settings', icon: 'fas fa-envelope', roles: ['core_admin'] },
        { id: 'templates', label: 'Email Templates', icon: 'fas fa-file-alt', roles: ['core_admin'] },
        { id: 'countries', label: 'Countries', icon: 'fas fa-globe', roles: ['core_admin'] },
        { id: 'cities', label: 'Cities', icon: 'fas fa-city', roles: ['core_admin'] },
        { id: 'sales-agents', label: 'Sales Agents', icon: 'fas fa-user-tie', roles: ['core_admin'] },
        { id: 'integrations', label: 'Integrations', icon: 'fas fa-plug', roles: ['core_admin'] },
        { id: 'designations', label: 'Designations', icon: 'fas fa-briefcase', roles: ['core_admin', 'client_admin', 'app_admin'] },
        { id: 'holidays', label: 'Public Holidays', icon: 'fas fa-calendar-day', roles: ['core_admin', 'client_admin', 'app_admin'] }
    ].filter(tab => tab.roles.includes(user?.user_type));

    // Set default active tab — honour navigation state (e.g. from dashboard "Manage Agents" link)
    const requestedTab = location.state?.activeTab;
    const defaultTab = (requestedTab && tabs.find(t => t.id === requestedTab))
        ? requestedTab
        : (tabs.length > 0 ? tabs[0].id : 'general');
    const [activeTab, setActiveTab] = useState(defaultTab);

    // Update active tab when user or tabs change
    useEffect(() => {
        if (tabs.length > 0 && !tabs.find(tab => tab.id === activeTab)) {
            setActiveTab(tabs[0].id);
        }
    }, [user, tabs, activeTab]);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'general':
                return <GeneralSettings />;
            case 'email':
                return <EmailSettings />;
            case 'templates':
                return <EmailTemplates />;
            case 'countries':
                return <CountryManagement />;
            case 'cities':
                return <CityManagement />;
            case 'sales-agents':
                return <SalesAgentManagement />;
            case 'integrations':
                return <IntegrationSettings />;
            case 'designations':
                return <DesignationManagement />;
            case 'holidays':
                return <PublicHolidayManagement />;
            default:
                return tabs.length > 0 ? null : <div>No settings available for your role.</div>;
        }
    };

    return (
        <div className="settings-page">
            <div className="page-header">
                <div className="header-content">
                    <h1 className="page-title">
                        <i className="fas fa-cog"></i>
                        System Settings
                    </h1>
                    <p className="page-description">
                        Manage your system configuration, branding, email settings, and templates
                    </p>
                </div>
            </div>

            <div className="settings-container">
                <div className="settings-tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <i className={tab.icon}></i>
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                <div className="settings-content">
                    {renderTabContent()}
                </div>
            </div>

            <style jsx>{`
                .settings-page {
                    padding: 2rem;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .page-header {
                    margin-bottom: 2rem;
                }

                .header-content {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    padding: 2rem;
                    border-radius: 16px;
                    color: white;
                    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
                }

                .page-title {
                    font-size: 2rem;
                    font-weight: 700;
                    margin: 0 0 0.5rem 0;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .page-title i {
                    font-size: 1.75rem;
                }

                .page-description {
                    margin: 0;
                    font-size: 1.1rem;
                    opacity: 0.95;
                }

                .settings-container {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                }
                .page-header p {
                color:white;
                }

                .card-header p {
                    color: white;
                }

                .settings-tabs {
                    display: flex;
                    background: #f8f9fa;
                    border-bottom: 2px solid #e9ecef;
                    padding: 0.4rem;
                    gap: 0.5rem;
                    overflow-x: auto;
                    overflow-y: hidden;
                    white-space: nowrap;
                    scrollbar-width: thin;
                }

                .settings-tab {
                    flex: 0 0 auto;
                    min-width: 140px;
                    padding: 0.7rem 0.9rem;
                    background: transparent;
                    border: none;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    font-size: 0.92rem;
                    font-weight: 600;
                    color: #6c757d;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .settings-tab:hover {
                    background: rgba(102, 126, 234, 0.1);
                    color: #667eea;
                }

                .settings-tab.active {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
                }

                .settings-tab i {
                    font-size: 1.2rem;
                }

                .settings-content {
                    padding: 2rem;
                    min-height: 500px;
                }

                @media (max-width: 768px) {
                    .settings-page {
                        padding: 1rem;
                    }

                    .settings-tabs {
                        flex-direction: row;
                        gap: 0.4rem;
                    }

                    .settings-tab {
                        justify-content: center;
                        min-width: 125px;
                        padding: 0.6rem 0.75rem;
                    }

                    .settings-content {
                        padding: 1.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default Settings;
